import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, ArrowRight, X } from 'lucide-react'
import { useToast } from './Toast'
import styles from './Footer.module.css'
import nexcomLogo from '../assets/Logo/Nexcom Logo.webp'

const LinkedinIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
)

const FacebookIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
)

const TwitterIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
  </svg>
)

const InstagramIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
)

export default function Footer() {
  const { addToast } = useToast()

  const handleContactClick = (e, type, value) => {
    e.preventDefault()

    if (type === 'email') {
      navigator.clipboard.writeText(value).then(() => {
        addToast('Email copied to clipboard')
      })
    } else if (type === 'phone') {
      navigator.clipboard.writeText(value.replace(/\s/g, '')).then(() => {
        addToast('Phone number copied')
      })
      setTimeout(() => {
        window.location.href = `tel:${value.replace(/\s/g, '')}`
      }, 1500)
    }
  }

  return (
    <footer className={styles.footer}>
      <div className="container">
        <div className={styles.mainRow}>
          {/* Logo & Socials */}
          <div className={styles.brandCol}>
            <Link to="/" className={styles.logoLink}>
              <img
                src={nexcomLogo}
                alt="Nexcom Logo"
                className={styles.footerLogo}
                loading="lazy"
              />
            </Link>
            <div className={styles.socials}>
              <a href="https://l.facebook.com/l.php?u=https%3A%2F%2Flinkedin.com%2Fin%2Fwww.linkedin.com%252Fcompany%252Fnexcompvtltd%3Ffbclid%3DIwZXh0bgNhZW0CMTAAYnJpZBExWXJqOFlkUEFoWk1aM1lOcQEeGycL5p_8gbpQsrrw_3G0ivDI4IhKEav1HlJWMj0eLkZSvoTXGbzK6pmoUlM_aem_DKxUJdU1lKyBstlONUigPg&h=AT2mzY1OU_wpsMHHeyeRkvyiXWwDTCZXF87erHy51YVlt1x9jsfun0snVKkLQSO6-sNdRfTbZWTXXTdDzN-bHouB6N-2Wls6kM6b_w8ACVf-riD9yEIovKa7tKZzYryEXgB2" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                <LinkedinIcon size={18} />
              </a>
              <a href="https://www.facebook.com/nexcom.net.pk/" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                <FacebookIcon size={18} />
              </a>
              <a href="https://www.instagram.com/nexcom.net.pk/" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                <InstagramIcon size={18} />
              </a>
            </div>
          </div>

          {/* Visit Us */}
          <div className={styles.infoCol}>
            <div className={styles.infoIconBox}>
              <MapPin size={20} />
            </div>
            <div className={styles.infoText}>
              <span className={styles.infoLabel}>VISIT US</span>
              <p>Office 789-C, Faisal Town,<br />Lahore, Pakistan</p>
            </div>
          </div>

          {/* Quick Contact */}
          <div className={styles.infoCol}>
            <div className={styles.infoIconBox}>
              <Phone size={20} />
            </div>
            <div className={styles.infoText}>
              <span className={styles.infoLabel}>QUICK CONTACT</span>
              <div className={styles.contactPerson}>
                <strong>Support Team</strong>
                <a
                  href="mailto:info@nexcom.net.pk"
                  onClick={(e) => handleContactClick(e, 'email', 'info@nexcom.net.pk')}
                  className={styles.contactSubLink}
                >
                  <Mail size={12} /> info@nexcom.net.pk
                </a>
              </div>
              <div className={styles.contactPerson}>
                <strong>Sales Inquiry</strong>
                <a
                  href="tel:+923211199937"
                  onClick={(e) => handleContactClick(e, 'phone', '+92 321 1199937')}
                  className={styles.contactSubLink}
                >
                  <Phone size={12} /> +92 321 1199937
                </a>
              </div>
            </div>
          </div>

          {/* Partners CTA */}
          <div className={styles.ctaCol}>
            <p>For further information or to partner with Nexcom, contact our technical team.</p>
            <a href="mailto:info@nexcom.net.pk" className={styles.emailBigLink} onClick={(e) => handleContactClick(e, 'email', 'info@nexcom.net.pk')}>
              <Mail size={18} /> info@nexcom.net.pk <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={styles.bottomBar}>
        <div className="container">
          <div className={styles.bottomInner}>
            <p>© 2026 NEXCOM. ALL RIGHTS RESERVED.</p>
            <div className={styles.bottomLinks}>
              <Link to="/privacy-policy">PRIVACY POLICY</Link>
              <Link to="/cookie-policy">COOKIE POLICY</Link>
              <Link to="/terms-conditions">TERMS & CONDITIONS</Link>
              <Link to="/sitemap">SITEMAP</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}